/**
 * Privacy-safe Plausible analytics helpers.
 * Fails silently when Plausible is not configured or the script is unavailable.
 */

/** Allowed custom property keys — no PII, no free-text user/listing identifiers. */
export const ALLOWED_ANALYTICS_PROP_KEYS = new Set([
  'country',
  'city',
  'property_type',
  'tla_eligible',
  'corporate_ready',
  'furnished',
  'source',
  'status',
]);

const BLOCKED_KEY_FRAGMENTS = [
  'email',
  'name',
  'phone',
  'address',
  'message',
  'title',
  'street',
  'owner',
  'password',
  'token',
  'user_id',
  'listing_id',
  'inquiry_id',
];

const MAX_PROP_LENGTH = 64;

function isBlockedKey(key) {
  const normalized = String(key || '').toLowerCase();
  return BLOCKED_KEY_FRAGMENTS.some((fragment) => normalized.includes(fragment));
}

function normalizePropValue(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    return trimmed.slice(0, MAX_PROP_LENGTH);
  }
  return null;
}

/**
 * Drop unknown keys and values that could carry PII or unstructured text.
 */
export function sanitizeAnalyticsProps(props = {}) {
  if (!props || typeof props !== 'object' || Array.isArray(props)) {
    return {};
  }

  const safe = {};
  for (const [key, value] of Object.entries(props)) {
    if (!ALLOWED_ANALYTICS_PROP_KEYS.has(key) || isBlockedKey(key)) {
      continue;
    }
    const normalized = normalizePropValue(value);
    if (normalized !== null) {
      safe[key] = normalized;
    }
  }
  return safe;
}

export function isPlausibleConfigured() {
  return Boolean(process.env.REACT_APP_PLAUSIBLE_DOMAIN);
}

export function isPlausibleAvailable() {
  return typeof window !== 'undefined' && typeof window.plausible === 'function';
}

/**
 * Track a custom Plausible event with sanitized props only.
 */
export function trackEvent(eventName, props = {}) {
  if (typeof window === 'undefined') return;
  if (!eventName || typeof eventName !== 'string') return;
  if (!isPlausibleAvailable()) return;

  try {
    const safeProps = sanitizeAnalyticsProps(props);
    if (Object.keys(safeProps).length > 0) {
      window.plausible(eventName, { props: safeProps });
    } else {
      window.plausible(eventName);
    }
  } catch {
    // Analytics must never break product flows.
  }
}

/**
 * Build safe listing metadata for property_view events (no title/address/ids).
 */
export function listingAnalyticsProps(listing, { source = 'property_detail' } = {}) {
  if (!listing || typeof listing !== 'object') {
    return { source };
  }

  const props = { source };

  if (listing.country_code) {
    props.country = String(listing.country_code).slice(0, 2).toUpperCase();
  }

  const locationName = String(listing.location_name || '').trim();
  if (locationName && !/\d+\s+\S/.test(locationName)) {
    props.city = locationName.split(',')[0].trim().slice(0, MAX_PROP_LENGTH);
  }

  if (listing.property_type) {
    props.property_type = String(listing.property_type).slice(0, MAX_PROP_LENGTH);
  }

  props.tla_eligible = Boolean(
    listing.tla_eligible === true || listing.tla_status === 'eligible',
  );

  if (listing.corporate_ready != null) {
    props.corporate_ready = Boolean(listing.corporate_ready);
  }

  if (listing.furnished != null) {
    props.furnished = Boolean(listing.furnished);
  } else if (listing.property_type) {
    props.furnished = /furnished/i.test(String(listing.property_type));
  }

  if (listing.status) {
    props.status = String(listing.status).slice(0, MAX_PROP_LENGTH);
  }

  return props;
}
